import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  filterAdvanced,
  filterSearch,
  orFilter,
} from "@west-tennessee-consulting/supabase/postgrest-filter";
import { getSupabase } from "@/lib/hooks/api/utils";

/** Value forms accepted by an advanced filter clause. */
type AdvancedFilterValue = string | number | boolean | Array<string | number>;

export interface DealFilterCriteria {
  nameSearch?: string;
  statusSearch?: string;
  stageSearch?: string;
  companySearch?: string;
  search?: string;
  advancedFilters?: Array<{ field: string; operator: string; value: AdvancedFilterValue }>;
}

/** A row returned by the deals export query (`select("*")`). */
interface DealExportRow {
  id: string;
  name?: string | null;
  company_name?: string | null;
  status?: string | null;
  stage?: string | null;
  amount?: number | null;
  expected_close_date?: string | null;
  actual_close_date?: string | null;
  owner_name?: string | null;
  description?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

// Helper function to build the same query as useDealsPaginated
function buildDealFilterQuery(
  supabase: ReturnType<typeof getSupabase>,
  filterCriteria: DealFilterCriteria
) {
  let query = supabase.from("deals").select("*", { count: "exact" });

  // Apply simple filters
  if (filterCriteria.statusSearch) {
    query = query.eq("status", filterCriteria.statusSearch);
  }
  if (filterCriteria.stageSearch) {
    query = query.eq("stage", filterCriteria.stageSearch);
  }

  // Handle generic filters that were spread from the filters object
  const knownFields = [
    "statusSearch",
    "stageSearch",
    "nameSearch",
    "companySearch",
    "search",
    "advancedFilters",
  ];
  Object.entries(filterCriteria).forEach(([key, value]) => {
    if (!knownFields.includes(key) && value !== undefined && value !== null && value !== "") {
      // Handle array values
      if (Array.isArray(value)) {
        query = query.contains(key, value);
      } else if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
      ) {
        query = query.eq(key, value);
      }
    }
  });

  // Specific field searches + global search term (OR-ed together)
  const specificSearchFilter = orFilter([
    filterSearch("name", filterCriteria.nameSearch ?? ""),
    filterSearch("company_name", filterCriteria.companySearch ?? ""),
    ...(filterCriteria.search
      ? [
          filterSearch("name", filterCriteria.search),
          filterSearch("company_name", filterCriteria.search),
        ]
      : []),
  ]);
  if (specificSearchFilter) {
    query = query.or(specificSearchFilter);
  }

  // Handle advanced filters
  if (filterCriteria.advancedFilters && filterCriteria.advancedFilters.length > 0) {
    const advancedFilter = orFilter(
      filterCriteria.advancedFilters.map(({ field, operator, value }) =>
        filterAdvanced(field, operator, value)
      )
    );
    if (advancedFilter) {
      query = query.or(advancedFilter);
    }
  }

  return query.order("created_at", { ascending: false });
}

// Get all deal IDs matching the filter criteria (with pagination)
async function getFilteredDealIds(
  filterCriteria: DealFilterCriteria
): Promise<Array<{ id: string; name: string }>> {
  const supabase = getSupabase();
  const pageSize = 1000; // Supabase limit per query
  let allDeals: Array<{ id: string; name: string }> = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    const query = buildDealFilterQuery(supabase, filterCriteria);
    const { data, error, count } = await query
      .select("id, name")
      .range(from, to);

    if (error) {
      console.error("Error fetching filtered deal IDs:", error);
      throw new Error(error.message);
    }

    if (!data || data.length === 0) {
      hasMore = false;
    } else {
      allDeals = allDeals.concat(data);
      // Check if we've fetched all records
      if (count !== null && allDeals.length >= count) {
        hasMore = false;
      } else if (data.length < pageSize) {
        hasMore = false;
      }
      page++;
    }
  }

  return allDeals;
}

// Bulk export deals by filter
export function useBulkExportDealsByFilter() {
  return useMutation({
    mutationFn: async ({ filterCriteria }: { filterCriteria: DealFilterCriteria }) => {
      const supabase = getSupabase();
      const query = buildDealFilterQuery(supabase, filterCriteria);

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching deals for export:", error);
        throw new Error(error.message);
      }

      if (!data || data.length === 0) {
        throw new Error("No deals found matching the current filters");
      }

      // Import CSV utilities dynamically
      const { objectsToCSV, downloadCSV, formatDateForCSV, formatNumberForCSV } =
        await import("@/lib/utils/csv-export");

      // Transform data for export
      const exportData = (data as DealExportRow[]).map((deal) => ({
        ID: deal.id,
        Name: deal.name || "",
        Company: deal.company_name || "",
        Status: deal.status || "",
        Stage: deal.stage || "",
        Amount: formatNumberForCSV(deal.amount),
        "Expected Close Date": formatDateForCSV(deal.expected_close_date),
        "Actual Close Date": formatDateForCSV(deal.actual_close_date),
        Owner: deal.owner_name || "",
        Description: deal.description || "",
        "Created At": formatDateForCSV(deal.created_at),
        "Updated At": formatDateForCSV(deal.updated_at),
      }));

      // Generate CSV
      const csvContent = objectsToCSV(exportData);
      const timestamp = new Date().toISOString().split("T")[0];
      downloadCSV(csvContent, `deals_export_${timestamp}.csv`);

      return { exported: data.length };
    },
    onSuccess: (result) => {
      toast.success(`${result.exported} deals exported successfully`);
    },
    onError: (error) => {
      toast.error("Failed to export deals");
      console.error("Error exporting deals:", error);
    },
  });
}

// Helper hook to convert filter state to DealFilterCriteria
export function useDealFilterCriteria(
  filters: Record<string, unknown>
): DealFilterCriteria {
  const asString = (value: unknown): string | undefined =>
    typeof value === "string" && value.length > 0 ? value : undefined;
  return {
    nameSearch: asString(filters.nameSearch),
    companySearch: asString(filters.companySearch),
    statusSearch: filters.status !== "all" ? asString(filters.status) : undefined,
    stageSearch: filters.stage !== "all" ? asString(filters.stage) : undefined,
  };
}
